import type { ContributorAssetType } from './contributor-types';
import { CONTRIBUTOR_ASSET_TYPES } from './contributor-types';

// Per-name upper bound on rows we expect from contributors.v1. The backend
// stores one row per asset type, so a single contributor returns at most
// `CONTRIBUTOR_ASSET_TYPES.length` rows. The +1 safety margin absorbs any
// transient extra-row emission from the indexer (e.g. mid-migration) so
// `per_page` doesn't silently drop rows. Used by `fetchAllByName` and
// `getContributorSlugsByNames`.
export const MAX_ROWS_PER_CONTRIBUTOR = CONTRIBUTOR_ASSET_TYPES.length + 1;

// `contributors.v1.contributor_type` uses short keys (e.g. `video` for stock
// video, mirroring the doc id pattern `<contributor_id>_<type>`) rather than
// the kebab-case `ContributorAssetType` strings the rest of the frontend uses.
// Each canonical asset type lists every raw value the indexer is known to emit
// — extra entries cover historical/alternative spellings so the indexer can
// move between conventions without breaking the lookup.
const TYPE_VARIANTS: Record<ContributorAssetType, readonly string[]> = {
  music: ['music'],
  sfx: ['sfx'],
  'stock-video': ['video', 'stock-video', 'stock_video', 'stockvideo'],
  'motion-graphics': [
    'motion',
    'motion-graphics',
    'motion_graphics',
    'motiongraphics',
  ],
  luts: ['lut', 'luts'],
};

const RAW_TO_ASSET_TYPE: ReadonlyMap<string, ContributorAssetType> = new Map(
  Object.entries(TYPE_VARIANTS).flatMap(([assetType, variants]) =>
    variants.map(
      (variant) => [variant, assetType as ContributorAssetType] as const
    )
  )
);

export const mapContributorType = (raw: string): ContributorAssetType | null =>
  RAW_TO_ASSET_TYPE.get(raw.toLowerCase()) ?? null;
